import { Core, WebViewerInstance } from '@pdftron/webviewer';

import {
  type FieldSuggestion,
  type PartBotResultJson,
  PART_BOT_RESULT_GROUPS,
} from '@frontend/api';
import type { ExtractedTableJson } from '@frontend/api/src/parts/useGetExtractedTables';

import { convertRectToPartBotLocation } from '../geomUtils';
import { type ISuggestionAnnotation } from './SuggestionAnnotation';
import {
  SUGGESTION_ANNOTATION_TYPES,
  SUGGESTION_ANNOTATION_CATEGORIES,
  ANNOTATION_COLOR_CODES,
  SUGGESTION_SUBJECT,
  ANNOTATION_OPACITY,
  ANNOTATION_BORDER_WIDTH,
} from './consts';
import {
  type SuggestionAnnotationRaw,
  type ActionTextSnippet,
  type TextPositionResult,
  type Geometry,
  type TextPositionGeometry,
  ANNOTATION_METADATA_TYPES,
  FieldSuggestionMetadata,
  ActionExtractionTextMetadata,
  FoundInFileMetadata,
  ExtractedTableMetadata,
  SuggestionAnnotationStateMap,
} from './types';

export const getCategoryColor = (category?: string): string => {
  if (!category) return ANNOTATION_COLOR_CODES.DEFAULT; // Default to setup color
  return (
    ANNOTATION_COLOR_CODES[category as keyof typeof ANNOTATION_COLOR_CODES] ||
    ANNOTATION_COLOR_CODES.DEFAULT
  );
};

export const getKeyForFieldSuggestion = (
  suggestion: FieldSuggestion,
): string => {
  return `${suggestion.fieldName}||${suggestion.id}`;
};

export const buildFieldSuggestionAnnotations = (
  suggestions: FieldSuggestion[],
  applicableFields?: Set<string>,
): SuggestionAnnotationRaw[] => {
  const out = [];
  const annotationFields =
    applicableFields ?? new Set(['part_number', 'revision', 'description']);
  for (const suggestion of suggestions) {
    if (!annotationFields.has(suggestion.fieldName)) continue;
    if (
      suggestion.sourceMetadata &&
      suggestion.sourceMetadata.page &&
      suggestion.sourceMetadata.loc
    ) {
      out.push({
        type: SUGGESTION_ANNOTATION_TYPES.FIELD_SUGGESTION,
        content: suggestion.suggestion as string,
        partFileId: suggestion.sourcePartFileId,
        id: getKeyForFieldSuggestion(suggestion),
        page: suggestion.sourceMetadata.page,
        loc: suggestion.sourceMetadata.loc,
        category: SUGGESTION_ANNOTATION_CATEGORIES.FIELD,
        colorCode: getCategoryColor(SUGGESTION_ANNOTATION_CATEGORIES.FIELD),
        opacity: ANNOTATION_OPACITY.DEFAULT_FILL,
        borderWidth: ANNOTATION_BORDER_WIDTH.DEFAULT,
        metadata: {
          type: ANNOTATION_METADATA_TYPES.FIELD_SUGGESTION,
          fieldSuggestion: suggestion,
        } as FieldSuggestionMetadata,
      });
    }
  }
  return out;
};

export const getKeyForTextExtraction = (
  actionId: string,
  text: string,
  partFileUuid: string,
  pageNumber: number,
  geometry: Geometry,
): string => {
  return `${actionId}||${text}||${partFileUuid}||${pageNumber}||{x1:${geometry.x1},y1:${geometry.y1},width:${geometry.width},height:${geometry.height}}`;
};

export const buildTextExtractionSuggestionAnnotations = (
  textExtractions: TextPositionResult[],
): SuggestionAnnotationRaw[] => {
  const out = [];
  for (const textExtraction of textExtractions) {
    for (const extractionGeometry of textExtraction.geometries) {
      const geometry = {
        x1: extractionGeometry.x,
        y1: extractionGeometry.y,
        width: extractionGeometry.width,
        height: extractionGeometry.height,
      };
      const actionId = textExtraction.snippet.actionUuid;
      const id = getKeyForTextExtraction(
        actionId,
        textExtraction.snippet.text,
        textExtraction.snippet.partFileUuid,
        textExtraction.snippet.pageNumber,
        geometry,
      );
      out.push({
        type: SUGGESTION_ANNOTATION_TYPES.ACTION_EXTRACTION_TEXT,
        content: textExtraction.snippet.text,
        partFileId: textExtraction.snippet.partFileUuid,
        id,
        page: textExtraction.snippet.pageNumber,
        geometry,
        loc: extractionGeometry.loc,
        // set category to action item to enable whiteout specific to action
        category: textExtraction.snippet.actionUuid,
        colorCode: ANNOTATION_COLOR_CODES.REQS_REVIEW,
        opacity: ANNOTATION_OPACITY.DEFAULT_FILL,
        borderWidth: ANNOTATION_BORDER_WIDTH.DEFAULT,
        metadata: {
          type: ANNOTATION_METADATA_TYPES.ACTION_EXTRACTION_TEXT,
          textPositionResult: textExtraction,
        } as ActionExtractionTextMetadata,
      });
    }
  }
  return out;
};

export const getKeyForPartBotResult = (result: PartBotResultJson): string => {
  if (result.uuid) {
    return result.uuid;
  }
  return `${result.category}||${result.value}||${result.partFileUuid}||${String(
    result.location,
  )}`;
};

export const buildFoundInFileAnnotations = (
  rawPartBotResults: PartBotResultJson[] | null,
): SuggestionAnnotationRaw[] => {
  return (rawPartBotResults ?? []).map((result) => ({
    type: SUGGESTION_ANNOTATION_TYPES.FOUND_IN_FILES,
    content: result.value as string,
    partFileId: result.partFileUuid,
    id: getKeyForPartBotResult(result),
    page: result.pageNumber,
    loc: result.location,
    // set category to whiteout based on inference group in FiF
    category: result.group,
    colorCode: getCategoryColor(result.group),
    opacity: ANNOTATION_OPACITY.DEFAULT_FILL,
    borderWidth: ANNOTATION_BORDER_WIDTH.DEFAULT,
    metadata: {
      type: ANNOTATION_METADATA_TYPES.FOUND_IN_FILES,
      partBotResult: result,
    } as FoundInFileMetadata,
    displayType: result.rawRegion ? 'region' : 'content',
    defaultRegionState: result.rawRegion
      ? {
          label: result.category.endsWith('s')
            ? result.category.slice(0, -1)
            : result.category,
          outlineOpacity: ANNOTATION_OPACITY.REGION_OUTLINE_DEFAULT,
          outlineWidth: ANNOTATION_BORDER_WIDTH.REGION_OUTLINE_DEFAULT,
          patternOpacity: ANNOTATION_OPACITY.REGION_PATTERN_DEFAULT,
        }
      : undefined,
  }));
};

export const getKeyForExtractedTableJson = (
  tableJson: ExtractedTableJson,
): string => {
  return `extractedTableKey-${tableJson.id}`;
};

export const buildExtractedTableAnnotations = (
  extractedTableResults: ExtractedTableJson[],
): SuggestionAnnotationRaw[] =>
  extractedTableResults.map((tableJson) => ({
    type: SUGGESTION_ANNOTATION_TYPES.EXTRACTED_TABLE,
    content: '',
    partFileId: tableJson.partFileId,
    id: getKeyForExtractedTableJson(tableJson),
    page: tableJson.pageNumber,
    loc: null,
    geometry:
      tableJson.x1 != null &&
      tableJson.y1 != null &&
      tableJson.width != null &&
      tableJson.height != null
        ? {
            x1: tableJson.x1,
            y1: tableJson.y1,
            width: tableJson.width,
            height: tableJson.height,
          }
        : null,
    // match Quote Setup category from FiF inferences
    category: PART_BOT_RESULT_GROUPS.QUOTE_SETUP,
    colorCode: getCategoryColor(PART_BOT_RESULT_GROUPS.QUOTE_SETUP),
    opacity: ANNOTATION_OPACITY.DEFAULT_FILL,
    borderWidth: ANNOTATION_BORDER_WIDTH.DEFAULT,
    metadata: {
      type: ANNOTATION_METADATA_TYPES.EXTRACTED_TABLE,
      tableJson,
    } as ExtractedTableMetadata,
    displayType: 'region',
    defaultRegionState: {
      label: tableJson.hasBom ? 'BOM' : 'Table',
      outlineOpacity: ANNOTATION_OPACITY.REGION_OUTLINE_DEFAULT,
      outlineWidth: ANNOTATION_BORDER_WIDTH.REGION_OUTLINE_DEFAULT,
      patternOpacity: ANNOTATION_OPACITY.REGION_PATTERN_DEFAULT,
    },
  }));

/**
 * Helper function to measure PDFTron's internal left panel width (thumbnails/pages panel).
 * When this panel is open, it shifts the document viewer area.
 *
 * @param documentViewer - The PDFTron document viewer instance
 * @returns The width of the left panel in pixels, or 0 if not open
 */
export const getPDFTronLeftPanelWidth = (): number => {
  // Try to find the leftPanel element within PDFTron's webviewer
  const webviewerElement = document.querySelector('apryse-webviewer');
  const leftPanel = webviewerElement?.shadowRoot?.querySelector(
    '[data-element="tabPanel"]',
  );

  if (!leftPanel) return 0;

  const leftPanelElement = leftPanel as HTMLElement;
  const rect = leftPanelElement.getBoundingClientRect();
  return rect.width;
};

export const isAnnotationVisible = (
  annotation: ISuggestionAnnotation,
  documentViewer: Core.DocumentViewer,
): boolean => {
  const pageNumber = annotation.getPageNumber();
  const pageCoordinates = annotation.getRect();

  // Get scroll container info
  const scrollViewElement = documentViewer.getScrollViewElement();
  const scrollViewRect = scrollViewElement.getBoundingClientRect();
  const scrollLeft = scrollViewElement.scrollLeft;
  const scrollTop = scrollViewElement.scrollTop;

  // Measure toolbar height to exclude it from visible area
  // The webviewer is a custom element (apryse-webviewer) with shadow DOM
  let totalHeaderHeight = 0;
  const webviewerElement = document.querySelector('apryse-webviewer');
  if (webviewerElement) {
    const webviewerRect = webviewerElement.getBoundingClientRect();
    // The difference between webviewer top and scrollView top is the toolbar height
    totalHeaderHeight = scrollViewRect.top - webviewerRect.top;
  }

  // Measure PDFTron's internal left panel width
  const leftPanelWidth = getPDFTronLeftPanelWidth();

  // Convert the page coordinates to window coordinates
  // Note: pageToWindow returns coordinates in the iframe/document space (not viewport space)
  const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();
  const windowCoord1 = displayMode.pageToWindow(
    { x: pageCoordinates.x1, y: pageCoordinates.y1 },
    pageNumber,
  );
  const windowCoord2 = displayMode.pageToWindow(
    { x: pageCoordinates.x2, y: pageCoordinates.y2 },
    pageNumber,
  );

  // Convert window coordinates to viewport coordinates by accounting for:
  // 1. The scroll view's position in the viewport (scrollViewRect.left/top)
  // 2. The current scroll position (scrollLeft/scrollTop)
  // 3. The toolbar height (totalHeaderHeight) - this effectively moves the top boundary down
  const viewportX1 = windowCoord1.x + scrollViewRect.left - scrollLeft;
  const viewportY1 =
    windowCoord1.y + scrollViewRect.top - totalHeaderHeight - scrollTop;
  const viewportX2 = windowCoord2.x + scrollViewRect.left - scrollLeft;
  const viewportY2 =
    windowCoord2.y + scrollViewRect.top - totalHeaderHeight - scrollTop;

  // Check if annotation is within the visible document area
  // Adjust boundaries to account for:
  // - Top: PDFTron toolbar
  // - Left: PDFTron internal left panel (thumbnails/pages)
  // - Right: PDFTron internal left panel (thumbnails/pages)
  // NOTE: we add to the visible right likely due to a viewport calculation bug in PDFTron,
  //  without this, annotations clearly in view near the right side of the pdf canvas will be
  //  considered not visible
  const visibleTop = scrollViewRect.top;
  const visibleBottom = scrollViewRect.bottom;
  const visibleLeft = scrollViewRect.left + leftPanelWidth;
  const visibleRight = scrollViewRect.right + leftPanelWidth;

  return (
    viewportX1 >= visibleLeft &&
    viewportX2 <= visibleRight &&
    viewportY1 >= visibleTop &&
    viewportY2 <= visibleBottom
  );
};

/**
 * Manually scrolls the document viewer to center an annotation in the visible area.
 *
 * This provides direct control over scrolling instead of relying on PDFTron's jumpToAnnotation.
 * The annotation will be centered within the document viewer's visible area, accounting for
 * any application panels or UI elements.
 *
 * @param annotation - The annotation to center (can be any PDFTron annotation type)
 * @param documentViewer - The PDFTron document viewer instance
 * @param webViewerInstance - The WebViewer instance
 * @param smooth - Whether to use smooth scrolling (default: true)
 */
export const scrollToAnnotation = (
  annotation: Core.Annotations.Annotation,
  documentViewer: Core.DocumentViewer,
  smooth = true,
): void => {
  const pageNumber = annotation.getPageNumber();
  const pageRect = annotation.getRect();

  // Calculate the center point of the annotation in page coordinates
  const annotationCenterX = (pageRect.x1 + pageRect.x2) / 2;
  const annotationCenterY = (pageRect.y1 + pageRect.y2) / 2;

  // Convert annotation center from page coordinates to window coordinates
  const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();
  const annotationCenterInWindow = displayMode.pageToWindow(
    { x: annotationCenterX, y: annotationCenterY },
    pageNumber,
  );

  // Get the scroll view element and its dimensions
  const scrollViewElement = documentViewer.getScrollViewElement();
  const scrollViewRect = scrollViewElement.getBoundingClientRect();

  // Measure PDFTron's internal left panel width to account for it
  const leftPanelWidth = getPDFTronLeftPanelWidth();

  // Calculate the center of the VISIBLE document area (excluding left panel)
  const visibleDocumentWidth = scrollViewRect.width;
  // When left panel is open, the centring is off - likely an internal bug with PDFTron
  const viewerCenterX = leftPanelWidth + visibleDocumentWidth / 2;
  const viewerCenterY = scrollViewRect.height / 2;

  // Calculate target scroll position to center the annotation
  // The annotation is at annotationCenterInWindow.x/y in the document
  // We want it to appear at viewerCenterX/Y in the visible area
  // So we need to scroll to: annotationPosition - viewerCenter
  const targetScrollLeft = annotationCenterInWindow.x - viewerCenterX;
  const targetScrollTop = annotationCenterInWindow.y - viewerCenterY;

  // Perform the scroll
  scrollViewElement.scrollTo({
    left: targetScrollLeft,
    top: targetScrollTop,
    behavior: smooth ? 'smooth' : 'auto',
  });
};

/**
 * Proxy for https://sdk.apryse.com/api/web/Core.Search.html
 */
type TextSearchResult = {
  ambientStr: string;
  pageNum: number;
  quads: { getPoints: () => Core.Math.Quad }[];
  resultCode: number;
  resultStr: string;
  resultStrEnd: number;
  resultStrStart: number;
};

/**
 * TextPositionSearcher handles searching for text snippets in PDF documents.
 *
 * The primary challenge this class solves is the search cancellation issue:
 * When multiple textSearchInit() calls are made in quick succession, each new search
 * cancels the previous one, resulting in incomplete or missing search results.
 *
 * This class provides two search strategies:
 * 1. Single search: For one snippet, uses the original optimized method
 * 2. Combined search: For multiple snippets, combines all patterns into one regex
 *    to avoid cancellation issues
 *
 * Pertinent apryse docs:
 * - https://docs.apryse.com/web/guides/advance-text-search
 */
export class TextPositionSearcher {
  private instance: WebViewerInstance;
  private documentViewer: Core.DocumentViewer;
  private onResultCallback: (result: TextPositionResult) => void;

  constructor(
    instance: WebViewerInstance,
    onResultCallback: (result: TextPositionResult) => void,
  ) {
    this.instance = instance;
    this.documentViewer = instance.Core.documentViewer;
    this.onResultCallback = onResultCallback;
  }

  /**
   * Escapes special regex characters in text to prevent regex syntax errors.
   *
   * When combining multiple text snippets into a single regex pattern, we need to
   * ensure that any special regex characters in the original text are properly escaped
   * so they are treated as literal text rather than regex operators.
   *
   * @param text - The text string to escape
   * @returns The escaped text safe for use in regex patterns
   */
  private _escapeRegex(text: string): string {
    // Escape special regex characters except for those we want to preserve
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Creates a combined regex pattern from multiple text snippets and builds a mapping
   * to track which snippet corresponds to each regex capture group.
   *
   * This method is the core of solving the search cancellation problem. Instead of
   * making multiple separate textSearchInit() calls (which cancel each other), we
   * create one complex regex that searches for all patterns simultaneously.
   *
   * The regex structure is: (pattern1)|(pattern2)|(pattern3)...
   * Each pattern is wrapped in a capturing group and escaped for safety.
   *
   * @param snippets - Array of text snippets to search for
   * @returns Object containing the combined regex and maps to track snippet origins
   */
  private _createCombinedRegex(snippets: ActionTextSnippet[]): {
    regex: string;
    snippetsByText: Map<string, ActionTextSnippet[]>;
    pageNumbers: Set<number>;
  } {
    const snippetsByText = new Map<string, ActionTextSnippet[]>();
    const pageNumbers = new Set<number>();
    const uniqueTexts: string[] = [];

    snippets.forEach((snippet) => {
      // Track all page numbers we need to search
      pageNumbers.add(snippet.pageNumber);

      // Group snippets by text content to handle duplicates across pages
      const existingSnippets = snippetsByText.get(snippet.text) || [];
      existingSnippets.push(snippet);
      snippetsByText.set(snippet.text, existingSnippets);

      // Only add unique text patterns to avoid duplicate regex groups
      if (existingSnippets.length === 1) {
        uniqueTexts.push(snippet.text);
      }
    });

    // Sort unique texts by length in descending order to prioritize longer matches
    // This prevents shorter patterns from matching when they are prefixes of longer patterns
    // e.g., "EXPORT" should not match when "EXPORT CONTROL" is also a pattern
    uniqueTexts.sort((a, b) => b.length - a.length);

    // Create regex parts from sorted unique texts
    const regexParts = uniqueTexts.map((text) => {
      const escapedText = this._escapeRegex(text);
      return `(${escapedText})`;
    });

    // Combine all patterns with OR operator
    const combinedRegex = regexParts.join('|');

    return { regex: combinedRegex, snippetsByText, pageNumbers };
  }

  /**
   * Finds the matching snippet(s) for a search result, handling cases where the same
   * text appears on multiple pages or multiple snippets have the same text.
   *
   * When a search result is found, we need to map it back to the original snippet(s)
   * that produced the match. This is complicated by the fact that:
   * 1. The same text might appear on multiple pages
   * 2. Multiple snippets might have identical text but different metadata
   * 3. We need to match based on both text content and page number
   *
   * @param resultStr - The text that was found by the search
   * @param pageNum - The page number where the text was found
   * @param snippetsByText - Map of text content to all snippets with that text
   * @returns Array of snippets that match the result text and page
   */
  private _findMatchingSnippets(
    resultStr: string,
    pageNum: number,
    snippetsByText: Map<string, ActionTextSnippet[]>,
  ): ActionTextSnippet[] {
    const candidateSnippets = snippetsByText.get(resultStr);
    if (!candidateSnippets) {
      return [];
    }

    // Filter snippets to only include those on the correct page
    return candidateSnippets.filter(
      (snippet) => snippet.pageNumber === pageNum,
    );
  }

  /**
   * Performs a combined search for multiple text snippets using a single regex pattern.
   *
   * This method solves the core search cancellation problem by consolidating multiple
   * textSearchInit() calls into a single search operation. When multiple searches are
   * initiated in quick succession, each new search cancels the previous one, leading to
   * incomplete results. By combining all search patterns into one regex, we ensure all
   * snippets are found in a single, uninterrupted search.
   *
   * The method handles complex scenarios:
   * - Snippets across multiple pages
   * - Duplicate text content on different pages
   * - Multiple snippets with identical text but different metadata
   *
   * @param snippets - Array of text snippets to search for across potentially multiple pages
   */
  private _performCombinedSearch(snippets: ActionTextSnippet[]): void {
    if (snippets.length === 0) return;

    const { Core } = this.instance;
    const { regex, snippetsByText, pageNumbers } =
      this._createCombinedRegex(snippets);
    const mode = [Core.Search.Mode.HIGHLIGHT | Core.Search.Mode.REGEX];

    // Determine the page range to search across all snippets
    const minPage = Math.min(...pageNumbers);
    const maxPage = Math.max(...pageNumbers);

    this.documentViewer.textSearchInit(regex, mode, {
      // fullSearch: true is REQUIRED - without it, the search stops at the first result
      // and we would miss subsequent matches for other snippets in our combined regex
      fullSearch: true,
      onResult: (res: TextSearchResult) => {
        if (res.resultCode === Core.Search.ResultCode.FOUND) {
          // Find which snippet(s) this result corresponds to
          // We need to check both text content and page number for accurate mapping
          const matchingSnippets = this._findMatchingSnippets(
            res.resultStr,
            res.pageNum,
            snippetsByText,
          );

          // Process each matching snippet (handles cases where same text appears multiple times)
          for (const matchingSnippet of matchingSnippets) {
            const geometries: TextPositionGeometry[] = [];

            for (const quad of res.quads) {
              const points = quad.getPoints();

              // Calculate min/max coordinates from the quad points
              const minX = Math.min(points.x1, points.x2, points.x3, points.x4);
              const maxX = Math.max(points.x1, points.x2, points.x3, points.x4);
              const minY = Math.min(points.y1, points.y2, points.y3, points.y4);
              const maxY = Math.max(points.y1, points.y2, points.y3, points.y4);

              const geometry = {
                x: minX,
                y: minY,
                width: maxX - minX,
                height: maxY - minY,
              };
              geometries.push({
                ...geometry,
                keyWithGeometry: getKeyForTextExtraction(
                  matchingSnippet.actionUuid,
                  matchingSnippet.text,
                  matchingSnippet.partFileUuid,
                  matchingSnippet.pageNumber,
                  {
                    x1: geometry.x,
                    y1: geometry.y,
                    width: geometry.width,
                    height: geometry.height,
                  },
                ),
                loc: convertRectToPartBotLocation(
                  {
                    x1: minX,
                    y1: minY,
                    x2: maxX,
                    y2: maxY,
                  },
                  this.documentViewer.getPageHeight(matchingSnippet.pageNumber),
                ),
              });
            }

            if (geometries.length > 0) {
              this.onResultCallback({
                snippet: matchingSnippet,
                geometries,
              });
            }
          }
        }
      },
      startPage: minPage,
      endPage: maxPage,
    });
  }

  /**
   * Finds text positions for one or more snippets across potentially multiple pages.
   *
   * The combined search approach is necessary because PDFTron's textSearchInit()
   * cancels any previous search when a new one is initiated. When searching for
   * multiple snippets in quick succession (e.g., in a forEach loop), only the last
   * search would complete, causing missed results.
   *
   * By combining all search patterns into a single regex, we ensure all snippets
   * are found in one uninterrupted search operation.
   *
   * @param snippets - Array of text snippets to search for (can span multiple pages)
   */
  public findTextPositionsOnPage(snippets: ActionTextSnippet[]): void {
    try {
      this._performCombinedSearch(snippets);
    } catch (error) {
      console.error('Error finding text positions on page', error);
    }
  }
}

/**
 * Temporarily hide SuggestionAnnotations and return the previous state
 */
export const temporarilyHideSuggestionAnnotations = (
  annotationManager: Core.AnnotationManager,
): {
  stateMap: SuggestionAnnotationStateMap;
  suggestionAnnotations: ISuggestionAnnotation[];
} => {
  const suggestionAnnotations = annotationManager
    .getAnnotationsList()
    .filter(
      (annot): annot is ISuggestionAnnotation =>
        annot.Subject === SUGGESTION_SUBJECT,
    );

  const stateMap: SuggestionAnnotationStateMap = {};
  for (const annot of suggestionAnnotations) {
    stateMap[annot.Id] = annot.getAppState();
    annot.hide();
    annotationManager.redrawAnnotation(annot);
  }

  return { stateMap, suggestionAnnotations };
};

/**
 * Restores previous state of SuggestionAnnotations
 */
export const restoreSuggestionAnnotations = (
  annotationManager: Core.AnnotationManager,
  suggestionAnnotations: ISuggestionAnnotation[],
  stateMap: SuggestionAnnotationStateMap,
): void => {
  for (const annot of suggestionAnnotations) {
    const prevState = stateMap[annot.Id];
    if (prevState) {
      annot.setAppState(prevState);
      annotationManager.redrawAnnotation(annot);
    }
  }
};

export type AnnotationViewportRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export const getAnnotationViewportRect = (
  annotation: Core.Annotations.Annotation,
  webViewerInstance: WebViewerInstance,
): AnnotationViewportRect | null => {
  const rect = annotation.getRect();
  const pageNumber = annotation.getPageNumber();
  const documentViewer = webViewerInstance.Core.documentViewer;
  const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();
  const scrollViewElement = documentViewer.getScrollViewElement();
  const scrollViewRect = scrollViewElement.getBoundingClientRect();
  const scrollLeft = scrollViewElement.scrollLeft;
  const scrollTop = scrollViewElement.scrollTop;
  let totalHeaderHeight = 0;
  const webviewerElement = document.querySelector('apryse-webviewer');
  if (webviewerElement) {
    const webviewerRect = webviewerElement.getBoundingClientRect();
    totalHeaderHeight = scrollViewRect.top - webviewerRect.top;
  }
  const leftPanelWidth = getPDFTronLeftPanelWidth();
  const topLeft = displayMode.pageToWindow(
    { x: rect.x1, y: rect.y1 },
    pageNumber,
  );
  const topRight = displayMode.pageToWindow(
    { x: rect.x2, y: rect.y1 },
    pageNumber,
  );
  const bottomLeft = displayMode.pageToWindow(
    { x: rect.x1, y: rect.y2 },
    pageNumber,
  );
  const toViewport = (coord: { x: number; y: number }) => ({
    x: coord.x + scrollViewRect.left - scrollLeft - leftPanelWidth,
    y: coord.y + scrollViewRect.top - totalHeaderHeight - scrollTop,
  });
  const viewportTopLeft = toViewport(topLeft);
  const viewportTopRight = toViewport(topRight);
  const viewportBottomLeft = toViewport(bottomLeft);
  const width = viewportTopRight.x - viewportTopLeft.x;
  const height = viewportBottomLeft.y - viewportTopLeft.y;
  return {
    left: viewportTopLeft.x,
    top: viewportTopLeft.y,
    width,
    height,
  };
};

/**
 * Dispatches a synthetic scroll event on the PDF document viewer's scroll
 * container. This tricks the PDFTron focus lifecycle into gracefully releasing
 * document focus, preventing persistent focus fighting when modals or dialogs
 * open over the viewer.
 */
export const releaseDocumentFocus = (
  documentViewer: Core.DocumentViewer | null,
): void => {
  if (!documentViewer) return;
  const scrollView = documentViewer.getScrollViewElement();
  if (scrollView) {
    scrollView.dispatchEvent(new Event('scroll', { bubbles: true }));
  }
};
